import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { Form } from 'antd';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Check, ChevronRight } from 'lucide-react';
import dayjs from 'dayjs';
import services from 'services';
import PersonalInfoForm from 'components/forms/doctors/PersonalInfoForm';
import ClinicalDetailsForm from 'components/forms/doctors/ClinicalDetailsForm';
import CredentialsForm from 'components/forms/doctors/CredentialsForm';
import localeTranslator from 'library/Hocs/LocalTranslator';
import Loader from 'components/AntEnhancement/Loader';
import '../NewDoctor/style.css';
var UpdateDoctor = function (props) {
    var t = props.translate;
    var navigate = useNavigate();
    var id = useParams().id;
    var doctorId = Number(id);
    var _a = useState(false), disabled = _a[0], setDisabled = _a[1];
    var form = Form.useForm()[0];
    var _b = useState(null), user = _b[0], setUser = _b[1];
    var _c = useState(false), prefilled = _c[0], setPrefilled = _c[1];
    var specializationService = services.specializationService, doctorService = services.doctorService;
    var _d = doctorService.useGetDoctor(doctorId), doctor = _d.data, isLoading = _d.isLoading;
    var specializations = specializationService.useSpecializations().data;
    var _e = doctorService.useUpdateDoctor(), updateDoctor = _e.mutate, requestStatus = _e.status;
    useEffect(function () {
        setUser(null);
        setPrefilled(false);
        form.resetFields();
    }, [doctorId, form]);
    useEffect(function () {
        if (!doctor || user)
            return;
        setUser(doctor.getUser());
    }, [doctor, user]);
    useEffect(function () {
        if (!doctor || !user || prefilled)
            return;
        var birth = user.birthday ? dayjs(user.getBirthDay()) : null;
        form.setFieldsValue({
            fullName: user.getName() || undefined,
            nationalId: doctor.getNationalId() || undefined,
            birthdate: (birth === null || birth === void 0 ? void 0 : birth.isValid()) ? birth : undefined,
            gender: user.getGender() || undefined,
            maritalStatus: doctor.getMartialStatus() || undefined,
            mobile: user.getMobile() || undefined,
            secondaryPhone: user.getHomePhone() && user.getHomePhone() !== '-'
                ? user.getHomePhone()
                : undefined,
            email: user.getEmail() || undefined,
            specialization: doctor.getSpecializationId() || undefined,
            address: user.getAddress() || undefined
        });
        setPrefilled(true);
    }, [doctor, user, form, prefilled]);
    var syncModelsFromForm = function () {
        var _a;
        if (!doctor || !user)
            return;
        var values = form.getFieldsValue(true);
        if (values.fullName)
            user.setName(String(values.fullName).trim());
        if (values.gender)
            user.setGender(String(values.gender));
        if (values.email != null)
            user.setEmail(String(values.email).trim());
        if (values.mobile)
            user.setMobile(String(values.mobile).trim());
        if (values.secondaryPhone != null) {
            user.setHomePhone(String(values.secondaryPhone).trim());
        }
        if (values.address != null)
            user.setAddress(String(values.address).trim());
        if (values.password) {
            user.setPassword(String(values.password));
            if (values.passwordConfirmation) {
                user.setPasswordConfirmation(String(values.passwordConfirmation));
            }
        }
        var birthdate = values.birthdate;
        if ((_a = birthdate === null || birthdate === void 0 ? void 0 : birthdate.isValid) === null || _a === void 0 ? void 0 : _a.call(birthdate)) {
            user.setBirthDay(birthdate.toDate());
        }
        if (values.nationalId)
            doctor.setNationalId(String(values.nationalId).trim());
        if (values.maritalStatus) {
            doctor.setMartialStatusId(String(values.maritalStatus));
        }
        if (values.specialization != null && specializations) {
            var specialization = specializations.find(function (item) { return item.getId() === Number(values.specialization); });
            if (specialization)
                doctor.setSpecialization(specialization);
        }
        doctor.setUser(user);
    };
    var submit = function () {
        if (!doctor)
            return;
        setDisabled(true);
        syncModelsFromForm();
        updateDoctor({
            doctor: doctor,
            successMessage: t('doctorUpdated')
        });
    };
    useEffect(function () {
        if (requestStatus === 'error') {
            setDisabled(false);
        }
    }, [requestStatus]);
    var scrollToFirstError = function () {
        form.scrollToField('fullName', { behavior: 'smooth' });
    };
    var goBack = function () { return navigate("/doctors/".concat(doctorId)); };
    return (_jsx(Loader, { renderSpinner: isLoading && !doctor, children: _jsxs("div", { className: "doctor-form-page", children: [_jsxs("header", { className: "doctor-form-page__header", children: [_jsxs("nav", { className: "doctor-form-page__breadcrumb", "aria-label": "Breadcrumb", children: [_jsx("span", { className: "doctor-form-page__crumb doctor-form-page__crumb--muted", children: t('breadcrumbPeople') }), _jsx(ChevronRight, { size: 13, strokeWidth: 2, "aria-hidden": true }), _jsx(Link, { to: "/doctors", className: "doctor-form-page__crumb", children: t('breadcrumbDoctors') }), _jsx(ChevronRight, { size: 13, strokeWidth: 2, "aria-hidden": true }), _jsx("span", { className: "doctor-form-page__crumb doctor-form-page__crumb--current", children: t('breadcrumbEdit') })] }), _jsxs("div", { className: "doctor-form-page__title-row", children: [_jsx("button", { type: "button", className: "doctor-form-page__back", onClick: goBack, "aria-label": t('common:buttons.cancel'), children: _jsx(ArrowLeft, { size: 18, strokeWidth: 2 }) }), _jsx("h1", { className: "doctor-form-page__title", children: t('editTitle') })] })] }), _jsx("div", { className: "doctor-form-page__canvas", children: doctor && user ? (_jsxs(Form, { className: "doctor-form", layout: "vertical", form: form, onFinish: submit, onFinishFailed: scrollToFirstError, requiredMark: false, children: [_jsx(PersonalInfoForm, { doctor: doctor, user: user }), specializations ? (_jsx(ClinicalDetailsForm, { doctor: doctor, user: user, specializations: specializations })) : null, _jsx(CredentialsForm, { user: user, requirePassword: false }), _jsxs("div", { className: "doctor-form-page__footer", children: [_jsxs("p", { className: "doctor-form-page__hint", children: [_jsx("span", { children: "*" }), " ", t('requiredHint')] }), _jsxs("div", { className: "doctor-form-page__footer-actions", children: [_jsx("button", { type: "button", className: "doctor-form-page__btn doctor-form-page__btn--ghost", onClick: goBack, children: t('common:buttons.cancel') }), _jsxs("button", { type: "submit", className: "doctor-form-page__btn doctor-form-page__btn--primary", disabled: disabled, children: [_jsx(Check, { size: 16, strokeWidth: 2 }), _jsx("span", { children: t('save') })] })] })] })] })) : null })] }) }));
};
export default localeTranslator(UpdateDoctor, 'doctorForm');
